/**
 * @file Container component for dynamically inject relation components in a detail.
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { ViewContainerRef, ComponentFactoryResolver, EventEmitter } from '@angular/core';
import { EntityType } from 'breeze-client';
import { ComponentRegistry } from '../../core/component-registry';
import { AbstractBase } from '../../core/helpers/abstract-base';
var RelationContainerComponent = /** @class */ (function (_super) {
    tslib_1.__extends(RelationContainerComponent, _super);
    /**
     * Creates an instance of RelationContainerComponent.
     * @memberOf RelationContainerComponent
     */
    function RelationContainerComponent(containerRef, factoryResolver) {
        var _this = _super.call(this) || this;
        _this.containerRef = containerRef;
        _this.factoryResolver = factoryResolver;
        /** Gets, sets the RelationComponent instance of this container. */
        _this.instance = null;
        /** Event triggered when modifications are committed. */
        _this.itemSaved = new EventEmitter();
        /**
         * Value of the overlayMode property
         * @private
         * @memberOf RelationContainerComponent
         */
        _this._overlayMode = false;
        _this.subscriptionPool.add(_this.itemSaved.subscribe(function (result) {
            if (!_this._component || !_this._component['itemSaved']) {
                return;
            }
            _this._component['itemSaved'](result);
        }));
        return _this;
    }
    Object.defineProperty(RelationContainerComponent.prototype, "hasChanges", {
        /**
         * Gets a value indicating whether the inner relation component has changes.
         * @readonly
         * @type {boolean}
         * @memberOf RelationContainerComponent
         */
        get: function () {
            return this.instance && this.instance.hasChanges;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationContainerComponent.prototype, "createMode", {
        /**
         * Gets a value indicating whether the inner relation is in create mode.
         * @readonly
         * @type {boolean}
         * @memberOf RelationContainerComponent
         */
        get: function () {
            return this.instance && this.instance.createMode;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationContainerComponent.prototype, "component", {
        /**
         * Gets the relation component
         * @readonly
         * @type {ChildComponent}
         * @memberOf RelationContainerComponent
         */
        get: function () {
            return this._component;
        },
        /**
         * Sets the relation component
         * @memberOf RelationContainerComponent
         */
        set: function (value) {
            var _this = this;
            this._component = value;
            if (this._component === undefined) {
                return;
            }
            if (this._component.component === undefined) {
                this._component.component = 'RelationCompoment';
            }
            var editor = ComponentRegistry.getComponent('relation', this._component.component);
            if (this.containerRef.length > 0) {
                this.containerRef.clear();
            }
            var componentFactory = this.factoryResolver.resolveComponentFactory(editor);
            var ref = this.containerRef.createComponent(componentFactory, 0);
            this.instance = ref.instance;
            this.instance.configuration = this._component.configuration;
            this.instance.itemSaved.subscribe(function (result) {
                _this.itemSaved.emit(result);
            });
            this.instance.overlayMode = this._overlayMode;
            this.instance.master = this._master;
            this.instance.masterType = this._masterType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationContainerComponent.prototype, "overlayMode", {
        /**
         * Gets the overlayMode of the relation component.
         * @type {boolean}
         * @memberOf RelationContainerComponent
         */
        get: function () {
            return this._overlayMode;
        },
        /**
         * Sets the overlayMode of the relation component.
         * @memberOf RelationContainerComponent
         */
        set: function (value) {
            this._overlayMode = value;
            if (this.instance) {
                this.instance.overlayMode = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationContainerComponent.prototype, "master", {
        /**
         * Gets the master entity.
         * @readonly
         * @type {Entity}
         * @memberOf RelationContainerComponent
         */
        get: function () {
            return this._master;
        },
        /**
         * Sets the master entity.
         * @memberOf RelationContainerComponent
         */
        set: function (value) {
            this._master = value;
            if (this.instance) {
                this.instance.master = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationContainerComponent.prototype, "masterType", {
        /**
         * Gets the master entity type.
         * @readonly
         * @type {EntityType}
         * @memberOf RelationContainerComponent
         */
        get: function () {
            return this._masterType;
        },
        /**
         * Sets the master entity type.
         * @memberOf RelationContainerComponent
         */
        set: function (value) {
            this._masterType = value;
            if (this.instance) {
                this.instance.masterType = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * This method is called when save button of the DetailComponent is clicked.
     * The RelationContainerComponent dispatch this event to the RelationComponent
     * @param {Event} event The click event.
     * @memberOf RelationContainerComponent
     */
    RelationContainerComponent.prototype.formSave = function (event) {
        if (this.instance) {
            this.instance.formSave(event);
        }
    };
    /**
     * This method is called when delete button of the DetailComponent is clicked.
     * The RelationContainerComponent dispatch this event to the RelationComponent
     * @param {Event} event The click event.
     * @memberOf RelationContainerComponent
     */
    RelationContainerComponent.prototype.formDelete = function (event) {
        if (this.instance) {
            this.instance.formDelete(event);
        }
    };
    /**
     * This method is called when cancel button of the DetailComponent is clicked.
     * The RelationContainerComponent dispatch this event to the RelationComponent
     * @param {Event} event The click event.
     * @memberOf RelationContainerComponent
     */
    RelationContainerComponent.prototype.formCancel = function (event) {
        if (this.instance) {
            this.instance.formCancel(event);
        }
    };
    return RelationContainerComponent;
}(AbstractBase));
export { RelationContainerComponent };
